import React from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { t } from 'ttag'
import { FormData } from '../../ChallengeAdmin'
import { FormGroup, IconButton, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import YumSwitch from '../../../../components/YumSwitch/YumSwitch'
import { DivisionCategoryRow, ProgressType } from '../../../../services/api.types'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { announce } from '../../../../components/a11y/useAnnouncer'

import { ReactComponent as IndividualInactiveIcon } from '../../../../assets/v3/icons/individual-inactive.svg'
import { ReactComponent as GroupInactiveIcon } from '../../../../assets/v3/icons/group-inactive.svg'
import { ReactComponent as CountryInactiveIcon } from '../../../../assets/v3/icons/country-inactive.svg'
import { ReactComponent as CollectiveTargetInactiveIcon } from '../../../../assets/v3/icons/collective-target-inactive.svg'

import { translateDivisionCategoryType } from '../../../TeamManagement/TeamManagement'
import { ActivityView } from '../../../Activity/store/ActivityReducer'
import { buildDivisionCategoryLeaderboardId, isActivityIndexType } from '../../../../common/helpers/challenges'
import { darkerGray, gray, lighterBlue } from '../../../../styles/material-ui-theme-v2'
import { tokens } from '../../../../styles/tokens'
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage'

const useStyles = makeStyles({
  disableHover: {
    '&.MuiListItem-button:hover': {
      backgroundColor: gray,
      borderColor: 'transparent',
    },
  },
  moveButton: {
    backgroundColor: '#ffffff',
    color: tokens.text.secondary,
    // The white fill is ~1.06:1 against the gray row, so the border is what marks
    // the button boundary (WCAG 1.4.11): text.secondary is 6.4:1 on the row and
    // matches the caret. border.default (#8A8EA5) is 2.97:1 there — just short.
    border: `1px solid ${tokens.text.secondary}`,
    '&:hover': {
      backgroundColor: lighterBlue,
      '@media (hover: none)': {
        backgroundColor: '#ffffff',
      },
    },
    // Boundary buttons are aria-disabled no-ops: darkerGray fill with a white
    // caret (4.9:1, WCAG 1.4.11), echoing the "off" switch style in the same rows.
    // No hover change, so they don't suggest interactivity.
    '&[aria-disabled="true"], &[aria-disabled="true"]:hover': {
      backgroundColor: darkerGray,
      borderColor: darkerGray,
      color: '#ffffff',
    },
  },
})

interface ChallengeStepLeaderboardsProps {
  register: UseFormRegister<FormData>
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  hasCountries: boolean
  divisionCategories: DivisionCategoryRow[]
  isLegacyChallenge: boolean
  useAltDragAndDrop: boolean
}

export default function ChallengeStepLeaderboards(props: ChallengeStepLeaderboardsProps) {
  const { watch, setValue, errors, hasCountries, divisionCategories, isLegacyChallenge, useAltDragAndDrop } = props
  const classes = useStyles()
  const [draggingId, setDraggingId] = React.useState<string | null>(null)
  const [showCountryHint, setShowCountryHint] = React.useState(false)

  // Stored enabled leaderboards (persisted when moving to next step).
  const leaderboards = watch('leaderboards') || []
  // Temporary ordered list with enabled flags (used only in this step).
  const leaderboardsDraft = watch('leaderboardsDraft') || []

  type LeaderboardOption = {
    id: string
    label: string
    icon: JSX.Element
    isCountry?: boolean
    isGroup?: boolean
  }

  const activityType = watch('activityType') as ProgressType | null
  const isActivityIndex = activityType ? isActivityIndexType(activityType) : false

  // Legacy challenges keep the original three tabs and do not allow ordering.
  // This mirrors the old UI for challenges without compact ranking view.
  const legacyOptions: LeaderboardOption[] = [
    { id: ActivityView.INDIVIDUAL, label: t`Individuals`, icon: <IndividualInactiveIcon /> },
    { id: ActivityView.GROUP, label: t`Teams`, icon: <GroupInactiveIcon />, isGroup: true },
    { id: ActivityView.COUNTRY, label: t`Countries`, icon: <CountryInactiveIcon />, isCountry: true },
  ]

  // Standard leaderboard options.
  // Collective is only available for activity index challenges.
  const standardOptions: LeaderboardOption[] = isLegacyChallenge
    ? legacyOptions
    : [
        { id: ActivityView.INDIVIDUAL, label: t`Individuals`, icon: <IndividualInactiveIcon /> },
        { id: ActivityView.GROUP, label: t`Teams`, icon: <GroupInactiveIcon />, isGroup: true },
        { id: ActivityView.COUNTRY, label: t`Countries`, icon: <CountryInactiveIcon />, isCountry: true },
        ...(!isActivityIndex
          ? [{ id: ActivityView.COLLECTIVE, label: t`Collective`, icon: <CollectiveTargetInactiveIcon /> }]
          : []),
      ]

  // Custom group leaderboard options are hidden for legacy challenges.
  const categoryOptions: LeaderboardOption[] = isLegacyChallenge
    ? []
    : divisionCategories
        .slice()
        .sort((a, b) => {
          const nameA = translateDivisionCategoryType(a, true)
          const nameB = translateDivisionCategoryType(b, true)
          return nameA.localeCompare(nameB)
        })
        .map((category) => {
          const categoryName = translateDivisionCategoryType(category, true)
          return {
            id: buildDivisionCategoryLeaderboardId(category.id),
            label: categoryName,
            // Use the group icon for now. This keeps the UI consistent.
            icon: <GroupInactiveIcon />,
          }
        })

  const allOptions: LeaderboardOption[] = [...standardOptions, ...categoryOptions]
  const optionsById = new Map(allOptions.map((option) => [option.id, option]))

  type LeaderboardDraftItem = {
    id: string
    enabled: boolean
  }

  const buildDraft = (draft: LeaderboardDraftItem[]): LeaderboardDraftItem[] => {
    // Keep the initial order aligned with the persisted goal leaderboards when available.
    // This ensures the form opens in the same order as the goal definition.
    const draftIds = draft.map((item) => item.id).filter((id) => optionsById.has(id))
    const goalIds = leaderboards.filter((id) => optionsById.has(id))
    const legacyOrderedIds = legacyOptions.map((option) => option.id)
    // Legacy challenges keep a fixed order to match the original experience.
    const orderedIds = isLegacyChallenge ? legacyOrderedIds : draftIds.length ? draftIds : goalIds

    // Append any missing options after the primary order.
    allOptions.forEach((option) => {
      if (!orderedIds.includes(option.id)) {
        orderedIds.push(option.id)
      }
    })

    const nextDraft = orderedIds.map((id) => {
      const existing = draft.find((item) => item.id === id)
      const option = optionsById.get(id)
      let enabled = existing ? existing.enabled : leaderboards.includes(id)

      // Disable country leaderboards when no countries are configured.
      if (!hasCountries && option?.isCountry) {
        enabled = false
      }

      return { id, enabled }
    })

    return nextDraft
  }

  const normalizeDraft = React.useCallback(
    (draft: LeaderboardDraftItem[]) => buildDraft(draft),
    [allOptions, leaderboards, hasCountries, isLegacyChallenge],
  )

  React.useEffect(() => {
    const normalized = normalizeDraft(leaderboardsDraft)
    const isSame =
      normalized.length === leaderboardsDraft.length &&
      normalized.every((item, index) => {
        const existing = leaderboardsDraft[index]
        return existing && existing.id === item.id && existing.enabled === item.enabled
      })

    if (!isSame) {
      setValue('leaderboardsDraft', normalized, { shouldDirty: true, shouldTouch: true })
    }
  }, [leaderboardsDraft, normalizeDraft, setValue])

  const normalizedDraft: LeaderboardDraftItem[] = normalizeDraft(leaderboardsDraft)
  // "Select at least one leaderboard" — raised on Next, not while toggling.
  const noneSelectedError = (errors.leaderboardsDraft as { message?: string } | undefined)?.message

  // Update the temporary ordered list in the form state. Once the "none selected" error is
  // showing, revalidate on every change so it clears as soon as a leaderboard is checked.
  const updateDraft = (nextDraft: LeaderboardDraftItem[]) => {
    setValue('leaderboardsDraft', nextDraft, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: !!noneSelectedError,
    })
  }

  const isToggleDisabled = (option: LeaderboardOption) => {
    if (option.isCountry && !hasCountries) return true
    return false
  }

  const handleToggle = (option: LeaderboardOption) => {
    const disabled = isToggleDisabled(option)
    if (disabled) {
      if (option.isCountry && !hasCountries) {
        setShowCountryHint(true)
      }
      return
    }
    setShowCountryHint(false)

    const nextDraft = normalizedDraft.map((item) => {
      if (item.id !== option.id) return item
      return { ...item, enabled: !item.enabled }
    })

    updateDraft(nextDraft)
  }

  // Simple reorder helper for drag-and-drop.
  const reorder = (list: LeaderboardDraftItem[], startIndex: number, endIndex: number): LeaderboardDraftItem[] => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    if (result.destination.index === result.source.index) return
    updateDraft(reorder(normalizedDraft, result.source.index, result.destination.index))
  }

  // Keyboard-accessible alternative to drag-and-drop (WCAG 2.1.1): arrow keys on the
  // focused drag handle, plus explicit "Move up"/"Move down" buttons. The buttons are
  // the path that works for screen readers in browse/virtual mode, where arrow keys
  // move the reading cursor and never reach the app. The new position is announced
  // via the aria-live region, and focus is restored to the same control after the
  // re-render so repeated presses keep moving the same item.
  const moveItem = (fromIndex: number, toIndex: number, label: string, focus: 'handle' | 'up' | 'down' = 'handle') => {
    if (toIndex < 0) {
      announce(t`${label} is already first.`)
      return
    }
    if (toIndex >= normalizedDraft.length) {
      announce(t`${label} is already last.`)
      return
    }
    const movedId = normalizedDraft[fromIndex]?.id
    updateDraft(reorder(normalizedDraft, fromIndex, toIndex))
    announce(t`${label} moved to position ${toIndex + 1} of ${normalizedDraft.length}.`)
    if (movedId) {
      const escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(movedId) : movedId
      // When the item reaches the top/bottom, the pressed caret becomes hidden —
      // fall back to the row's other caret so keyboard focus isn't dropped.
      const candidates =
        focus === 'up'
          ? ['data-leaderboard-move-up', 'data-leaderboard-move-down']
          : focus === 'down'
            ? ['data-leaderboard-move-down', 'data-leaderboard-move-up']
            : ['data-leaderboard-handle']
      setTimeout(() => {
        for (const attribute of candidates) {
          const el = document.querySelector<HTMLElement>(`[${attribute}="${escapedId}"]`)
          if (el && getComputedStyle(el).visibility !== 'hidden') {
            el.focus()
            return
          }
        }
      }, 0)
    }
  }

  const renderOption = (
    option: LeaderboardOption,
    isEnabled: boolean,
    disableHover: boolean,
    reorderControl?: 'drag' | 'buttons',
    dragHandleProps?: any,
    index?: number,
  ) => {
    const toggleDisabled = isToggleDisabled(option)

    return (
      <>
        <ListItem
          role={undefined}
          dense
          button
          onClick={() => handleToggle(option)}
          className={disableHover ? classes.disableHover : undefined}
        >
          {reorderControl === 'buttons' && typeof index === 'number' && (
            <ListItemIcon style={{ minWidth: 'auto', marginRight: 8 }}>
              <IconButton
                size="small"
                className={classes.moveButton}
                data-leaderboard-move-up={option.id}
                title={t`Move ${option.label} up`}
                aria-label={t`Move ${option.label} up`}
                aria-disabled={index === 0}
                style={{ marginRight: 4 }}
                onClick={(event) => {
                  event.stopPropagation()
                  moveItem(index, index - 1, option.label, 'up')
                }}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                className={classes.moveButton}
                data-leaderboard-move-down={option.id}
                title={t`Move ${option.label} down`}
                aria-label={t`Move ${option.label} down`}
                aria-disabled={index === normalizedDraft.length - 1}
                onClick={(event) => {
                  event.stopPropagation()
                  moveItem(index, index + 1, option.label, 'down')
                }}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </ListItemIcon>
          )}
          {reorderControl === 'drag' && (
            <ListItemIcon
              {...(dragHandleProps || {})}
              data-leaderboard-handle={option.id}
              title={t`Reorder ${option.label}`}
              aria-label={t`Reorder ${option.label}. Press the up or down arrow key to move it.`}
              style={{ minWidth: 24, marginRight: 8, cursor: 'grab' }}
              onKeyDown={(event: React.KeyboardEvent) => {
                const isArrow = event.key === 'ArrowUp' || event.key === 'ArrowDown'
                // When not in an active drag, arrow keys reorder directly (WCAG 2.1.1).
                if (isArrow && !draggingId && typeof index === 'number') {
                  event.preventDefault()
                  moveItem(index, event.key === 'ArrowUp' ? index - 1 : index + 1, option.label)
                  return
                }
                // Otherwise defer to react-beautiful-dnd (space to lift, then arrows).
                dragHandleProps?.onKeyDown?.(event)
              }}
            >
              <DragIndicatorIcon />
            </ListItemIcon>
          )}
          <ListItemIcon>{option.icon}</ListItemIcon>
          <ListItemText primary={option.label} />
          <ListItemIcon>
            <YumSwitch
              checked={isEnabled}
              inputProps={{ 'aria-label': option.label }}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                event.stopPropagation()
                handleToggle(option)
              }}
              disabled={toggleDisabled}
            />
          </ListItemIcon>
        </ListItem>
        {option.isCountry && !hasCountries && showCountryHint && (
          <ErrorMessage>{t`Countries need to be added first`}</ErrorMessage>
        )}
      </>
    )
  }
  // Legacy challenges must keep the original ordering rules, so we disable drag-and-drop.
  const shouldAllowOrdering = !isLegacyChallenge

  return (
    <>
      <FormGroup>
        {/* ALT_DRAG_AND_DROP: reorder with move up/down buttons instead of drag-and-drop.
            Plain buttons work for keyboard and screen reader users in every mode (WCAG 2.1.1). */}
        {shouldAllowOrdering && useAltDragAndDrop && (
          <div>
            {normalizedDraft.map((item, index) => {
              const option = optionsById.get(item.id)
              if (!option) return null
              return (
                <div key={option.id} style={{ marginBottom: 12 }}>
                  {renderOption(option, item.enabled, false, 'buttons', undefined, index)}
                </div>
              )
            })}
          </div>
        )}
        {/* Sortable list for modern challenges. */}
        {shouldAllowOrdering && !useAltDragAndDrop && (
          <DragDropContext
            onDragStart={(start) => setDraggingId(start.draggableId)}
            onDragEnd={(result) => {
              setDraggingId(null)
              handleDragEnd(result)
            }}
          >
            <Droppable droppableId="leaderboards">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {normalizedDraft.map((item, index) => {
                    const option = optionsById.get(item.id)
                    if (!option) return null
                    const isEnabled = item.enabled
                    const disableHover = !!draggingId && draggingId !== option.id
                    return (
                      <Draggable key={option.id} draggableId={option.id} index={index}>
                        {(draggableProvided) => (
                          // Apply draggable style on the same element as draggableProps to avoid jump.
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{ marginBottom: 12, ...draggableProvided.draggableProps.style }}
                          >
                            {renderOption(
                              option,
                              isEnabled,
                              disableHover,
                              'drag',
                              draggableProvided.dragHandleProps,
                              index,
                            )}
                          </div>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
        {/* Fixed order list for legacy challenges. */}
        {!shouldAllowOrdering && (
          <div>
            {normalizedDraft.map((item) => {
              const option = optionsById.get(item.id)
              if (!option) return null
              const isEnabled = item.enabled
              return (
                <div key={option.id} style={{ marginBottom: 12 }}>
                  {renderOption(option, isEnabled, false)}
                </div>
              )
            })}
          </div>
        )}
      </FormGroup>
      {noneSelectedError && <ErrorMessage>{noneSelectedError}</ErrorMessage>}
      {isLegacyChallenge && (
        <div style={{ textAlign: 'center', opacity: 0.5, marginTop: '12px' }}>
          {t`Tip: Create a new challenge for more leaderboard options.`}
        </div>
      )}
    </>
  )
}
